import React, { useCallback, type ReactNode } from 'react';
import Button from '@atlaskit/button';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, Stack, xcss, Text } from '@atlaskit/primitives';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { useAssociateField } from '@atlassian/jira-business-inline-field-config-sidebar/src/controllers/associate-field/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import messages from './messages.tsx';

const POPUP_OFFSET: [number, number] = [-8, 8];

type Props = {
	isOpen: boolean;
	issueType?: string;
	issueTypeId?: string;
	fieldId: string;
	fieldName: string;
	onClose: () => void;
};

export const AssociateFieldPopup = ({
	isOpen,
	issueType,
	issueTypeId,
	fieldId,
	fieldName,
	onClose,
}: Props) => {
	const project = useProject();
	const { associate } = useAssociateField();
	const { formatMessage } = useIntl();

	const canAssociateField =
		project.permissions.administerProject &&
		project.isSimplified &&
		issueType !== undefined &&
		issueTypeId !== undefined;

	const popupTrigger = useCallback(
		({
			ref,
			'aria-expanded': ariaExpanded,
			'aria-haspopup': ariaHaspopup,
			'aria-controls': ariaControls,
		}: TriggerProps) => (
			<Box
				ref={ref}
				aria-expanded={ariaExpanded}
				aria-haspopup={ariaHaspopup}
				aria-controls={ariaControls}
				xcss={triggerStyles}
				role="button"
				aria-label={formatMessage(messages.linkGoals)}
			/>
		),
		[formatMessage],
	);

	const onAssociateField = useCallback(() => {
		associate({
			input: {
				projectId: String(project.id),
				fieldId,
				issueTypeIds: [Number(issueTypeId)],
			},
			fieldName,
			issueType,
			showSuccess: true,
			analyticsAttributes: {
				source: 'business-list.shared.fields.goals-cell',
			},
		});
		onClose();
	}, [associate, fieldId, fieldName, issueType, issueTypeId, onClose, project.id]);

	const content = useCallback(
		() => (
			<Box>
				<Stack space="space.100" xcss={popupStyles}>
					<Box>
						{formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.associateGoalsFieldBodyIssueTermRefresh
								: messages.associateGoalsFieldBody,
							{
								issueType,
								fieldName,
								bold: (chunk: ReactNode) => <Text as="strong">{chunk}</Text>,
							},
						)}
					</Box>
					<Box>
						{canAssociateField ? (
							<Button onClick={onAssociateField} appearance="link" spacing="none">
								{formatMessage(messages.updateFieldAssociationAction)}
							</Button>
						) : (
							<Button
								href="/secure/ContactAdministrators!default.jspa"
								target="_blank"
								onClick={onClose}
								appearance="link"
								spacing="none"
							>
								{formatMessage(messages.contactAdmin)}
							</Button>
						)}
					</Box>
				</Stack>
			</Box>
		),
		[canAssociateField, fieldName, formatMessage, issueType, onAssociateField, onClose],
	);

	return (
		<Popup
			isOpen={isOpen}
			content={content}
			trigger={popupTrigger}
			offset={POPUP_OFFSET}
			placement="bottom-start"
			messageId="business-fields.ui.goals.cell.associate-field-popup.popup"
			messageType="transactional"
			onClose={onClose}
		/>
	);
};

const popupStyles = xcss({
	width: '300px',
	paddingTop: 'space.200',
	paddingBottom: 'space.200',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
});

const triggerStyles = xcss({
	display: 'flex',
	height: '100%',
	width: '100%',
});
